import { BlogArticle } from '../../types/blog';

// Blog articles data
export const ARTICLES: BlogArticle[] = [
  {
    id: '5',
    slug: 'novi-val-yugoslav-new-wave-music-movement',
    title: 'Novi Val: The Yugoslav New Wave That Rivaled the West',
    subtitle: 'How Yugoslavia\'s underground music scene created a sound that defined a generation',
    metaDescription: 'Discover Novi Val, Yugoslavia\'s vibrant New Wave music movement of the 1980s. From Šarlo Akrobata to Idoli, explore the bands, sound, and cultural impact of Yugoslav New Wave.',
    author: 'Born in YU Archives',
    publishDate: '2026-02-02',
    category: 'culture',
    featuredImage: '/blog/novi-val-yugoslav-new-wave.jpg',
    featuredImageAlt: 'Yugoslav New Wave band performing in the 1980s - Novi Val music scene',
    readTime: '11 min read',
    seoKeywords: [
      'Novi Val',
      'Yugoslav New Wave',
      'Yugoslav music',
      'Šarlo Akrobata',
      'Idoli',
      'Yugoslav punk',
      'Yugowave',
      '80s Yugoslav bands',
      'Yugoslav rock',
    ],
    tags: ['Music', 'Culture', 'New Wave', 'Novi Val', '1980s', 'Underground'],
    relatedProducts: ['8'], // YU-008 Yugowave shirt
    content: `
      <p class="lead text-xl font-medium">While the West had Talking Heads, Joy Division, and Depeche Mode, Yugoslavia was brewing its own revolution. Novi Val—literally "New Wave"—exploded from Belgrade, Zagreb, and Ljubljana in the early 1980s, proving that behind the Iron Curtain, creativity couldn't be contained. This is the story of the movement that gave birth to what we now call Yugowave.</p>

      <h2>The Birth of Novi Val: 1980-1982</h2>
      <p>The death of Tito in May 1980 marked more than a political transition—it unleashed a cultural earthquake. Young Yugoslavs, raised on smuggled Western records and Radio Luxembourg broadcasts, were ready to create something of their own. They didn't want to imitate the West; they wanted to respond to it.</p>

      <p>The movement crystallized in three cities simultaneously:</p>
      <ul>
        <li><strong>Belgrade:</strong> Raw, intellectual, and confrontational</li>
        <li><strong>Zagreb:</strong> Polished, synth-driven, and art-school influenced</li>
        <li><strong>Ljubljana:</strong> Industrial, experimental, and provocative</li>
      </ul>

      <p>Unlike punk, which burned bright and fast, Novi Val had staying power. These bands wrote songs that still resonate today—not just as nostalgia, but as genuinely timeless music.</p>

      <hr class="my-8 border-black" />

      <h2>The Belgrade Scene: Intellectual Rebellion</h2>

      <h3>Šarlo Akrobata: The Godfathers</h3>
      <p>No discussion of Novi Val begins without Šarlo Akrobata. Formed in 1980, the band featured Milan Mladenović (who would later form Ekatarina Velika) and Dušan Kojić "Koja" (future frontman of Disciplina Kičme). Their 1981 album "Bistriji ili tuplji čovek biva kad..." remains a landmark of Yugoslav rock.</p>

      <p>Šarlo Akrobata's sound was sparse, urgent, and literary. Mladenović's lyrics drew from poetry and philosophy, while Koja's bass lines created a foundation that was both minimal and mesmerizing. They proved that Yugoslav bands could be intellectual without being inaccessible.</p>

      <p><strong>Essential track:</strong> "Ona se budi" — a song that captures the restless energy of early-80s Belgrade in under three minutes.</p>

      <h3>Idoli: Art-Rock Meets Pop Sensibility</h3>
      <p>If Šarlo Akrobata were the intellectuals, Idoli were the artists. Fronted by the charismatic Vlada Divljan, with Srđan Šaper's distinctive keyboards and Zdenko Kolar's driving drums, Idoli created a sound that was simultaneously avant-garde and catchy.</p>

      <p>Their 1982 album "Odbrana i poslednji dani" (Defense and the Last Days) is often cited as one of the greatest Yugoslav albums ever made. The title track, an 8-minute epic, demonstrated that Yugoslav bands could match the ambition of anything coming out of London or New York.</p>

      <p><strong>Essential track:</strong> "Maljčiki" — a synth-pop masterpiece that could have been a hit anywhere in the world.</p>

      <h3>Električni Orgazam: The Accessible Edge</h3>
      <p>Električni Orgazam brought Novi Val to the masses. Their sound was more straightforward rock, but with new wave's angular energy and sardonic wit. Songs like "Igra rokenrol cela Jugoslavija" (All of Yugoslavia Plays Rock and Roll) became anthems that transcended the underground.</p>

      <p>The band proved that Novi Val wasn't just for art students and intellectuals—it could fill stadiums while still maintaining its edge.</p>

      <hr class="my-8 border-black" />

      <h2>The Zagreb Scene: Synthesizers and Style</h2>

      <h3>Film: The Sound of the Future</h3>
      <p>Zagreb's contribution to Novi Val was distinctly different from Belgrade's. Bands like Film embraced synthesizers and electronic production in ways that anticipated the sound of the mid-80s. Their polished, almost clinical approach created a fascinating contrast with Belgrade's rawness.</p>

      <p>Film's "Sva čuda svijeta" (All the Wonders of the World) showcased a band equally comfortable with pop hooks and experimental soundscapes. Jura Stublić's vocals had an emotional intensity that cut through the electronic arrangements.</p>

      <h3>Haustor: Jazz-Inflected New Wave</h3>
      <p>Haustor defied easy categorization. Led by the brilliant Darko Rundek, the band incorporated jazz, world music, and theatrical elements into their new wave foundation. Their 1982 debut "Haustor" remains one of the most sophisticated albums of the era.</p>

      <p>Rundek's lyrics were poetic and surreal, while the band's arrangements were complex without being pretentious. Haustor proved that Novi Val could be intellectually challenging and emotionally engaging simultaneously.</p>

      <h3>Azra: The Bridge Between Eras</h3>
      <p>While technically predating Novi Val, Azra—led by the legendary Johnny Štulić—became inextricably linked with the movement. Štulić was Yugoslavia's closest equivalent to a rock poet, his lyrics dissecting society, love, and existence with surgical precision.</p>

      <p>Azra's influence on Novi Val was immeasurable. They demonstrated that Yugoslav rock could be both commercially successful and artistically uncompromising.</p>

      <hr class="my-8 border-black" />

      <h2>The Ljubljana Scene: Industrial Provocateurs</h2>

      <h3>Laibach: Controversy as Art</h3>
      <p>No band from the Yugoslav Novi Val era generated more controversy—or proved more internationally successful—than Ljubljana's Laibach. Formed in 1980 in the mining town of Trbovlje, Laibach used totalitarian imagery, industrial sounds, and confrontational performances to challenge both Western and Eastern political systems.</p>

      <p>Their aesthetic was deliberately ambiguous: were they criticizing totalitarianism or celebrating it? This ambiguity was the point. Laibach forced audiences to confront their own assumptions about power, ideology, and art.</p>

      <p>While often banned in Yugoslavia, Laibach achieved international recognition, eventually becoming Slovenia's most famous musical export. Their cover albums—reinterpreting everyone from the Beatles to the Rolling Stones through their industrial lens—became cult classics.</p>

      <h3>Videosex: Synth-Pop Pioneers</h3>
      <p>On the lighter end of Ljubljana's spectrum, Videosex created infectious synth-pop that rivaled anything coming out of the UK. Their sound was sleek, modern, and unabashedly electronic—a stark contrast to the rock-oriented Belgrade scene.</p>

      <hr class="my-8 border-black" />

      <h2>The Sound of Novi Val</h2>

      <h3>Musical Characteristics</h3>
      <p>What made Novi Val distinctive? Several elements combined to create the movement's signature sound:</p>

      <ul>
        <li><strong>Angular guitars:</strong> Influenced by Gang of Four and Wire, Yugoslav guitarists favored jagged, rhythmic playing over traditional rock heroics</li>
        <li><strong>Prominent bass:</strong> The bass often carried the melody, influenced by Joy Division's Peter Hook</li>
        <li><strong>Synthesizers:</strong> From minimal touches to full electronic arrangements, synths were integral to the sound</li>
        <li><strong>Literary lyrics:</strong> Yugoslav bands drew from poetry, philosophy, and literature in ways that distinguished them from Western counterparts</li>
        <li><strong>Urgency:</strong> A sense that something important was being communicated, that this music mattered</li>
      </ul>

      <h3>The Yugoslav Difference</h3>
      <p>What separated Novi Val from Western new wave? Context. These musicians operated in a unique environment—not quite Eastern Bloc, not quite Western. They had access to Western music and culture that their counterparts in Poland or Czechoslovakia could only dream of, yet they still operated under a socialist system with its own constraints and contradictions.</p>

      <p>This in-between status gave Novi Val its distinctive tension. The music couldn't be openly political in Western terms, so it became oblique, metaphorical, and poetic. This restraint often made it more powerful, not less.</p>

      <hr class="my-8 border-black" />

      <h2>Key Albums of the Novi Val Era</h2>

      <p>For those wanting to explore the movement, these albums are essential:</p>

      <ul>
        <li><strong>Šarlo Akrobata - "Bistriji ili tuplji čovek biva kad..." (1981)</strong> — The blueprint for Belgrade's sound</li>
        <li><strong>Idoli - "Odbrana i poslednji dani" (1982)</strong> — Art-rock ambition meets new wave energy</li>
        <li><strong>Električni Orgazam - "Električni Orgazam" (1981)</strong> — Accessible yet edgy debut</li>
        <li><strong>Film - "Sva čuda svijeta" (1982)</strong> — Zagreb synth-pop at its finest</li>
        <li><strong>Haustor - "Haustor" (1982)</strong> — Jazz-inflected sophistication</li>
        <li><strong>Azra - "Sunčana strana ulice" (1981)</strong> — Rock poetry from Johnny Štulić</li>
        <li><strong>Laibach - "Laibach" (1985)</strong> — Industrial provocation</li>
        <li><strong>Videosex - "Videosex" (1984)</strong> — Pure synth-pop pleasure</li>
      </ul>

      <hr class="my-8 border-black" />

      <h2>The Legacy: From Novi Val to Yugowave</h2>

      <h3>What Came After</h3>
      <p>By the mid-1980s, Novi Val as a distinct movement had evolved. Some bands, like Ekatarina Velika (formed from Šarlo Akrobata's ashes), continued to push boundaries throughout the decade. Others disbanded or moved in different directions.</p>

      <p>The wars of the 1990s scattered the Yugoslav music community. Musicians found themselves on opposite sides of new borders, and the shared cultural space that had nurtured Novi Val ceased to exist. Yet the music survived—passed down through recordings, memories, and an enduring sense that something special had happened.</p>

      <h3>The Yugowave Revival</h3>
      <p>In recent years, there's been a renaissance of interest in Yugoslav culture—and Novi Val sits at the heart of it. The term "Yugowave" has emerged to describe this aesthetic: a blend of Yugoslav nostalgia with synthwave and retrowave visual styles.</p>

      <p>Young people who never experienced Yugoslavia are discovering Novi Val through streaming platforms and YouTube. They're drawn to its authenticity, its energy, and its refusal to be easily categorized. In an age of algorithmic conformity, Novi Val's eccentricity feels refreshing.</p>

      <p>The neon sunsets, the retro typography, the sense of a lost future—Yugowave captures both the optimism of socialist modernism and the melancholy of its disappearance. It's not just nostalgia; it's a recognition that something valuable existed and deserves to be remembered.</p>

      <hr class="my-8 border-black" />

      <h2>Where to Start</h2>

      <p>If you're new to Novi Val, here's a suggested listening path:</p>

      <ol>
        <li><strong>Start with Idoli's "Maljčiki"</strong> — Immediately accessible, undeniably catchy</li>
        <li><strong>Move to Šarlo Akrobata's "Ona se budi"</strong> — Feel the raw Belgrade energy</li>
        <li><strong>Try Film's "Tema za jednu televizijsku špicu"</strong> — Zagreb sophistication</li>
        <li><strong>Experience Azra's "Kurvini sinovi"</strong> — Štulić at his most passionate</li>
        <li><strong>Dive deep with Idoli's "Odbrana i poslednji dani"</strong> — The full album experience</li>
        <li><strong>Explore Laibach's "Geburt einer Nation"</strong> — If you're ready for the industrial edge</li>
      </ol>

      <hr class="my-8 border-black" />

      <h2>The Music Lives On</h2>

      <p>Novi Val wasn't just a musical movement—it was proof that creativity transcends political systems and geographical boundaries. These bands, working with limited resources in a country that no longer exists, created art that still resonates four decades later.</p>

      <p>For those Born in Yugoslavia, this music is the soundtrack of memory—of late nights, of first loves, of a world that seemed full of possibility. For those discovering it now, it's a window into a culture that produced something genuinely unique.</p>

      <p>The synthesizers still pulse. The angular guitars still cut. The voices still speak to something universal about youth, rebellion, and the search for meaning. Novi Val lives on.</p>

      <p><em>Embrace the Yugowave aesthetic with our <a href="/product/born-in-yugoslavia-tshirt-retro-synthwave">Born in Yugoslavia Retro Synthwave T-Shirt</a>. Featuring neon sunsets and Yugoslav nostalgia, it's the perfect tribute to the era that gave us Novi Val.</em></p>
    `,
  },
  {
    id: '4',
    slug: 'yugoslav-expressions-balkan-linguistic-heritage',
    title: 'The Art of Yugoslav Expression: A Linguistic Guide to Balkan Profanity',
    subtitle: 'Understanding the colorful language that united six republics',
    metaDescription: 'Explore the rich linguistic heritage of Yugoslav expressions and colorful phrases. From everyday sayings to passionate exclamations, discover the words that defined Balkan communication.',
    author: 'Born in YU Archives',
    publishDate: '2026-01-20',
    category: 'culture',
    featuredImage: '/blog/yugoslav-expressions-linguistic-heritage.jpg',
    featuredImageAlt: 'Vintage Yugoslav street scene representing everyday communication and cultural expression',
    readTime: '9 min read',
    seoKeywords: [
      'Yugoslav expressions',
      'Balkan phrases',
      'Serbian expressions',
      'Croatian sayings',
      'Yugoslav culture',
      'Balkan language',
      'Yugoslav slang',
    ],
    tags: ['Culture', 'Language', 'Humor', 'Heritage', 'Linguistics'],
    relatedProducts: ['5', '6'], // Will link to new expression products
    content: `
      <p class="lead text-xl font-medium">If you were Born in Yugoslavia, you grew up with a linguistic heritage that was... expressive, to say the least. The colorful phrases that echoed through Yugoslav households, kafanas, and streets weren't just words—they were an art form. Today, we explore the linguistic gems that connected millions across six republics.</p>

      <h2>A Language of Passion</h2>
      <p>Yugoslavs were never known for holding back their feelings. Whether expressing frustration, affection, or complete indifference, the language of the Balkans offered a rich vocabulary that could make poets blush and linguists take notes. These weren't just swear words—they were cultural expressions that communicated volumes about the speaker's emotional state.</p>

      <p>What makes Yugoslav expressions unique is their creativity. Rather than simple expletives, these phrases often paint vivid pictures, invoke family members, and demonstrate a level of linguistic imagination that's genuinely impressive.</p>

      <hr class="my-8 border-black" />

      <h2>The Philosophy of "Boli Me..."</h2>
      <p>Perhaps no phrase better captures the Yugoslav attitude than the family of expressions beginning with "Boli me..." (It hurts me...). These phrases don't indicate actual physical pain—instead, they communicate a profound level of not caring about something.</p>

      <h3>The Classic: "Boli Me Kurac"</h3>
      <p>Literally translated, this would be anatomically concerning. In practice, it's the ultimate expression of indifference. When a Yugoslav says this, they're communicating that they could not possibly care less about the matter at hand. It's not anger—it's transcendence beyond caring.</p>

      <p><strong>Usage context:</strong> When your boss asks you to work overtime on a Saturday. When someone gossips about you. When presented with any problem that doesn't directly affect your well-being.</p>

      <h3>Regional Variations</h3>
      <p>This expression, like many Yugoslav phrases, has regional variations:</p>
      <ul>
        <li><strong>Serbian:</strong> Delivered with a dismissive wave of the hand</li>
        <li><strong>Croatian:</strong> Often accompanied by a shoulder shrug</li>
        <li><strong>Bosnian:</strong> Frequently paired with a sip of coffee</li>
        <li><strong>Slovenian:</strong> Translated to local equivalent with similar effect</li>
      </ul>

      <hr class="my-8 border-black" />

      <h2>The Maternal Reference: A Balkan Specialty</h2>
      <p>Yugoslav expressions have a particular fondness for maternal references. "Idi u pičku materinu" and its variations are perhaps the most versatile phrases in the Balkan linguistic arsenal.</p>

      <h3>Understanding the Context</h3>
      <p>Despite the literal translation, these phrases are used in contexts ranging from genuine anger to playful banter among friends. Context is everything:</p>

      <ul>
        <li><strong>Angry context:</strong> Said forcefully during a traffic incident or heated argument</li>
        <li><strong>Friendly context:</strong> Said with a laugh when a friend makes an outrageous joke</li>
        <li><strong>Surprised context:</strong> Exclaimed when seeing something unexpected (positive or negative)</li>
        <li><strong>Dismissive context:</strong> Used to end a conversation you're done with</li>
      </ul>

      <h3>The Art of Delivery</h3>
      <p>A true Yugoslav knows that these phrases are all about delivery. The same words can be a serious insult or a term of endearment depending on:</p>
      <ul>
        <li>Tone of voice</li>
        <li>Facial expression</li>
        <li>Relationship with the recipient</li>
        <li>Regional accent</li>
        <li>Accompanying hand gestures</li>
      </ul>

      <hr class="my-8 border-black" />

      <h2>The Kafana Dictionary</h2>
      <p>Yugoslav kafanas (traditional coffee houses and taverns) developed their own linguistic ecosystem. These establishments were incubators for creative expression, where ordinary phrases were elevated to poetry.</p>

      <h3>Common Kafana Expressions</h3>
      <ul>
        <li><strong>"Jebem ti sunce"</strong> - A cosmic expression of frustration</li>
        <li><strong>"Koji kurac?"</strong> - The Yugoslav equivalent of "What the...?"</li>
        <li><strong>"Ma puši kurac"</strong> - Dismissive, often used when rejecting an offer or idea</li>
        <li><strong>"Mrš u pičku materinu"</strong> - A more emphatic send-off</li>
        <li><strong>"Jebo te pas"</strong> - An expression of disbelief or exasperation</li>
      </ul>

      <h3>The Evolution of "Jebiga"</h3>
      <p>"Jebiga" deserves special mention as perhaps the most versatile word in the Yugoslav vocabulary. This single expression can mean:</p>
      <ul>
        <li>"Oh well" (resignation)</li>
        <li>"What can you do?" (acceptance)</li>
        <li>"Life happens" (philosophy)</li>
        <li>"I'm sorry but not really" (apology)</li>
        <li>"That's just how it is" (explanation)</li>
      </ul>
      <p>It's the perfect response to any of life's difficulties—a one-word summary of the Yugoslav approach to adversity.</p>

      <hr class="my-8 border-black" />

      <h2>Cross-Cultural Understanding</h2>
      <p>One of the fascinating aspects of Yugoslav expressions is how they traveled across republic borders. Despite different dialects and scripts, these phrases were universally understood from Ljubljana to Skopje.</p>

      <h3>The Unity of Expression</h3>
      <p>While Yugoslavia's republics had their differences, they shared a common expressive vocabulary. A Serb, a Croat, a Bosniak, a Slovenian, a Macedonian, and a Montenegrin could all understand—and appreciate—the same colorful expressions. This linguistic unity was perhaps more powerful than any political slogan.</p>

      <h3>The Diaspora Connection</h3>
      <p>Today, these expressions live on in Yugoslav diaspora communities worldwide. In Toronto, Melbourne, Chicago, and Berlin, second and third-generation Yugoslavs still use these phrases—often as the first "heritage language" words they learn from their parents and grandparents.</p>

      <hr class="my-8 border-black" />

      <h2>Beyond Profanity: Everyday Expressions</h2>
      <p>Not all Yugoslav expressions were profane. The language included many colorful sayings that captured the Yugoslav worldview:</p>

      <ul>
        <li><strong>"Ko te šiša?"</strong> - "Who's cutting your hair?" (Who cares about you?)</li>
        <li><strong>"Nije to med i mleko"</strong> - "It's not honey and milk" (It's not easy)</li>
        <li><strong>"Gde si, brate?"</strong> - "Where are you, brother?" (Common greeting)</li>
        <li><strong>"Ma daj, bre!"</strong> - "Come on, man!" (Expression of disbelief)</li>
        <li><strong>"Ajde, ajde"</strong> - "Let's go, let's go" (Encouragement or hurrying)</li>
      </ul>

      <hr class="my-8 border-black" />

      <h2>The Linguistic Heritage Today</h2>
      <p>These expressions represent more than just colorful language—they're a window into Yugoslav culture and mentality. The directness, the humor, the creative imagery, and the passionate delivery all tell us something about the people who used them.</p>

      <h3>Why These Phrases Endure</h3>
      <p>Even decades after Yugoslavia's dissolution, these expressions persist because they:</p>
      <ul>
        <li>Express emotions more effectively than "polite" alternatives</li>
        <li>Create instant connection among those who share the heritage</li>
        <li>Carry cultural memory and identity</li>
        <li>Simply feel good to say (ask any linguist about the satisfaction of certain consonant clusters)</li>
      </ul>

      <h3>A Word on Context</h3>
      <p>Understanding when and how to use these expressions is crucial. Among friends and family, they're terms of endearment and shared cultural identity. In professional settings... perhaps stick to "jebiga" at most.</p>

      <hr class="my-8 border-black" />

      <h2>Wear Your Heritage (Literally)</h2>
      <p>For those who want to celebrate this linguistic heritage, there's something wonderfully subversive about wearing these expressions on a t-shirt. It's a badge of cultural identity, a conversation starter with fellow Yugoslavs, and—let's be honest—a way to express yourself that most people around you won't even understand.</p>

      <p>Whether you choose the philosophical "Boli Me Kurac" (expressing transcendent indifference) or the classic "Jebiga" (the one-word Yugoslav philosophy), wearing these expressions is a way to carry your heritage with pride.</p>

      <p><em>Ready to wear your heritage? Check out our <a href="/products">Yugoslav Expressions Collection</a> featuring the colorful language that united six republics.</em></p>
    `,
  },
  {
    id: '3',
    slug: 'k67-kiosk-yugoslav-design-icon',
    title: 'The K67 Kiosk: Yugoslavia\'s Most Iconic Design Export',
    subtitle: 'How a modular kiosk from Slovenia conquered the world',
    metaDescription: 'Discover the K67 kiosk, Yugoslavia\'s most successful design export. From MoMA to street corners worldwide, explore the legacy of Saša J. Mächtig\'s revolutionary 1967 modular design.',
    author: 'Born in YU Archives',
    publishDate: '2026-01-13',
    category: 'design',
    featuredImage: '/blog/k67-kiosk-yugoslav-design.jpg',
    featuredImageAlt: 'K67 modular kiosk designed by Saša J. Mächtig in 1967, iconic Yugoslav design',
    readTime: '10 min read',
    seoKeywords: [
      'K67 kiosk',
      'Saša J. Mächtig',
      'Yugoslav design',
      'modular kiosk',
      'Yugoslav architecture',
      'K67 design',
      'socialist design',
    ],
    tags: ['Design', 'Architecture', 'K67', 'Innovation', 'Industrial Design'],
    relatedProducts: ['4'], // K67 Poster
    content: `
      <p class="lead text-xl font-medium">In 1967, Slovenian architect Saša J. Mächtig designed something extraordinary: a modular kiosk that would become one of Yugoslavia's most successful design exports. The K67 kiosk represents the pinnacle of socialist-era innovation—functional, beautiful, and exported to over 50 countries worldwide.</p>

      <h2>The Birth of an Icon: 1966-1967</h2>
      <p>In 1966, architect Saša J. Mächtig began work on a revolutionary concept: a fully modular, transportable kiosk system. Patented in 1967, the K67 (named after its patent year) was designed to solve a practical problem—Yugoslavia needed a standardized, durable retail solution that could be rapidly deployed across diverse environments.</p>

      <p>What Mächtig created was far more than a simple newsstand. The K67 was a masterpiece of modular design, constructed from polyfibre-reinforced plastic panels that could be assembled without tools in just a few hours. Its bright orange-red color became iconic, making it instantly recognizable on street corners from Ljubljana to New York City.</p>

      <hr class="my-8 border-black" />

      <h2>Revolutionary Design Features</h2>

      <h3>Modularity at Its Core</h3>
      <p>The genius of the K67 lay in its modular construction. Each kiosk consisted of standardized panels that could be combined in multiple configurations:</p>

      <ul>
        <li><strong>Basic unit:</strong> 2.0 x 2.0 x 2.4 meters</li>
        <li><strong>Expandable design:</strong> Multiple units could be connected to create larger spaces</li>
        <li><strong>Removable panels:</strong> Windows, doors, and serving hatches could be positioned as needed</li>
        <li><strong>Tool-free assembly:</strong> Four people could assemble a complete unit in 2-3 hours</li>
      </ul>

      <h3>Material Innovation</h3>
      <p>The K67 utilized polyfibre-reinforced plastic, a cutting-edge material in the 1960s that offered:</p>
      <ul>
        <li>Weather resistance (from Arctic cold to tropical heat)</li>
        <li>Durability (kiosks from the 1970s are still in use today)</li>
        <li>Lightweight construction (easy to transport and relocate)</li>
        <li>Low maintenance (no rust, no rot, minimal upkeep)</li>
        <li>Vibrant color retention (the iconic orange-red stayed bright for decades)</li>
      </ul>

      <hr class="my-8 border-black" />

      <h2>Global Success: 7,500 Units Across 50+ Countries</h2>

      <p>From 1968 to 1999, approximately 7,500 K67 kiosks were produced by the Slovenian company Iskra. The design became Yugoslavia's most successful industrial design export, shipped to countries across six continents:</p>

      <h3>Europe</h3>
      <ul>
        <li><strong>Yugoslavia:</strong> Thousands deployed across all republics</li>
        <li><strong>Poland:</strong> Major urban centers in Warsaw, Kraków, Gdańsk</li>
        <li><strong>Soviet Union:</strong> Moscow, Leningrad (St. Petersburg), and other cities</li>
        <li><strong>Germany:</strong> Both East and West Germany purchased units</li>
      </ul>

      <h3>Beyond Europe</h3>
      <ul>
        <li><strong>United States:</strong> New York City, San Francisco, and other major cities</li>
        <li><strong>Japan:</strong> Tokyo and other metropolitan areas</li>
        <li><strong>New Zealand:</strong> Wellington and Auckland</li>
        <li><strong>Kenya:</strong> Nairobi and coastal cities</li>
        <li><strong>Iraq:</strong> Baghdad and other urban centers</li>
      </ul>

      <h3>Versatile Applications</h3>
      <p>The K67's modular design made it perfect for multiple uses:</p>
      <ul>
        <li>Newspaper and magazine stands</li>
        <li>Coffee and refreshment kiosks</li>
        <li>Flower shops</li>
        <li>Information booths</li>
        <li>Ticket offices</li>
        <li>Street food vendors</li>
        <li>Small retail shops</li>
      </ul>

      <hr class="my-8 border-black" />

      <h2>Cultural Recognition and Museum Collections</h2>

      <h3>MoMA Permanent Collection</h3>
      <p>The Museum of Modern Art (MoMA) in New York City recognized the K67's design significance by including it in their permanent architecture and design collection. This honor placed the K67 alongside the world's most important design objects—a testament to Yugoslav industrial design excellence.</p>

      <h3>"Toward a Concrete Utopia" Exhibition</h3>
      <p>In 2018-2019, MoMA's groundbreaking exhibition "Toward a Concrete Utopia: Architecture in Yugoslavia, 1948-1980" prominently featured the K67 kiosk. The exhibition introduced a global audience to the sophistication and innovation of Yugoslav architecture and design, with the K67 serving as a prime example of practical socialist modernism.</p>

      <h3>Design Museums Worldwide</h3>
      <p>Beyond MoMA, K67 kiosks are featured in design museums across Europe, including:</p>
      <ul>
        <li>Museum of Architecture and Design (MAO), Ljubljana, Slovenia</li>
        <li>Design Museum, London, UK</li>
        <li>Vitra Design Museum, Weil am Rhein, Germany</li>
      </ul>

      <hr class="my-8 border-black" />

      <h2>The K67 Today: A Living Legacy</h2>

      <h3>Still in Use After 50+ Years</h3>
      <p>One of the most remarkable aspects of the K67 is its longevity. Original kiosks from the 1970s and 1980s continue to operate on streets across former Yugoslavia and beyond. The durability of Mächtig's design means that many owners have never needed to replace their kiosks—they simply maintain and occasionally repaint them.</p>

      <h3>Cultural Renaissance</h3>
      <p>In recent years, the K67 has experienced a cultural renaissance:</p>
      <ul>
        <li><strong>Ljubljana Revival:</strong> The city restored several K67 kiosks to their original condition, now serving specialty coffee and artisan goods</li>
        <li><strong>Design Hotels:</strong> Some units have been converted into ultra-compact hotel rooms</li>
        <li><strong>Pop-Up Spaces:</strong> Designers worldwide use K67 units for temporary installations and events</li>
        <li><strong>Cultural Symbols:</strong> The kiosks have become symbols of Yugoslav design excellence and socialist-era innovation</li>
      </ul>

      <h3>Where to Find K67 Kiosks Today</h3>
      <p>If you want to see a K67 in person, visit these cities:</p>
      <ul>
        <li><strong>Ljubljana, Slovenia:</strong> Multiple restored units throughout the city center</li>
        <li><strong>Belgrade, Serbia:</strong> Original units still operating as newsstands</li>
        <li><strong>Zagreb, Croatia:</strong> Several kiosks in the city center</li>
        <li><strong>Sarajevo, Bosnia and Herzegovina:</strong> Original 1970s units still in use</li>
        <li><strong>Warsaw, Poland:</strong> Soviet-era K67s serving coffee and snacks</li>
      </ul>

      <hr class="my-8 border-black" />

      <h2>Design Philosophy: Socialist Modernism at Its Best</h2>

      <h3>Form Follows Function</h3>
      <p>The K67 embodies the socialist design principle that good design serves the people. Mächtig created something that was:</p>
      <ul>
        <li>Affordable to produce</li>
        <li>Easy to maintain</li>
        <li>Adaptable to any environment</li>
        <li>Accessible to small business owners</li>
        <li>Beautiful without being elitist</li>
      </ul>

      <h3>Democratic Design</h3>
      <p>Unlike Western designs that often prioritized aesthetics over accessibility, the K67 was designed to be owned and operated by ordinary people. Its low cost and minimal maintenance made small-scale entrepreneurship possible for thousands of Yugoslavs.</p>

      <h3>Internationalist Vision</h3>
      <p>The K67's success across cultures and climates reflected Yugoslavia's internationalist ideology. A kiosk designed in Slovenia worked equally well in Kenya, Japan, or New York—a truly universal design that transcended borders.</p>

      <hr class="my-8 border-black" />

      <h2>Saša J. Mächtig: The Designer</h2>

      <p>Saša J. Mächtig (1927-2011) was a Slovenian architect and designer who believed in design's power to improve everyday life. Born in Ljubljana, he studied architecture at the University of Ljubljana and worked on numerous projects throughout Yugoslavia.</p>

      <p>While Mächtig designed many buildings and public spaces, the K67 kiosk became his most enduring legacy. He once said: "I wanted to create something that would serve people in their daily lives—not a monument to architecture, but a tool for living."</p>

      <p>His philosophy of democratic, functional design influenced a generation of Yugoslav architects and designers who believed that socialist society should produce objects of beauty and utility for everyone, not just the elite.</p>

      <hr class="my-8 border-black" />

      <h2>The K67 in Popular Culture</h2>

      <p>The K67 has appeared in:</p>
      <ul>
        <li><strong>Films:</strong> Numerous Yugoslav films from the 1970s-1990s feature K67 kiosks as part of street scenes</li>
        <li><strong>Photography:</strong> The distinctive orange-red color made it a favorite subject for street photographers</li>
        <li><strong>Music Videos:</strong> Contemporary Balkan musicians use K67 kiosks as nostalgic backdrops</li>
        <li><strong>Art Installations:</strong> Contemporary artists have repurposed K67 units for gallery exhibitions</li>
      </ul>

      <hr class="my-8 border-black" />

      <h2>Why the K67 Matters Today</h2>

      <h3>Lessons in Sustainable Design</h3>
      <p>In an era of planned obsolescence, the K67 offers important lessons:</p>
      <ul>
        <li><strong>Durability over disposability:</strong> Design things to last decades, not years</li>
        <li><strong>Modularity over replacement:</strong> Create systems that can be adapted and expanded</li>
        <li><strong>Universal design:</strong> Create objects that work in any cultural context</li>
        <li><strong>Material innovation:</strong> Choose materials for longevity and environmental performance</li>
      </ul>

      <h3>A Symbol of Yugoslav Excellence</h3>
      <p>The K67 represents everything Yugoslavia achieved at its best: innovation, internationalism, quality manufacturing, and democratic design principles. It proves that socialist societies could produce world-class industrial design that competed with—and often surpassed—Western alternatives.</p>

      <hr class="my-8 border-black" />

      <h2>Preserve the Legacy</h2>

      <p>The K67 kiosk is more than just a clever design—it's a symbol of an era when Yugoslavia produced world-class innovation. From street corners in Ljubljana to MoMA's galleries, the K67 continues to inspire designers and remind us that great design serves everyone.</p>

      <p>Today, as original K67 units age and face demolition, preservation efforts are underway across former Yugoslavia. Design museums, cultural organizations, and individual enthusiasts are working to save these icons of socialist modernism for future generations.</p>

      <p><em>Celebrate this design masterpiece with our <a href="/product/k67-modular-kiosk-poster-yugoslav-design">K67 Modular Kiosk Poster</a>. This museum-quality print honors Saša J. Mächtig's revolutionary design and brings a piece of Yugoslav design history into your home.</em></p>
    `,
  },
  {
    id: '2',
    slug: 'what-it-means-to-be-born-in-yugoslavia',
    title: 'What It Means to Be Born in Yugoslavia',
    subtitle: 'A shared identity that transcends borders and time',
    metaDescription: 'Discover what it means to be Born in Yugoslavia. Explore the shared identity, cultural heritage, and collective memory that unites millions across the former Yugoslav republics.',
    author: 'Born in YU Archives',
    publishDate: '2026-01-13',
    category: 'culture',
    featuredImage: '/blog/born-in-yugoslavia-identity.jpg',
    featuredImageAlt: 'People gathering at a Yugoslav monument representing shared heritage',
    readTime: '8 min read',
    seoKeywords: [
      'Born in Yugoslavia',
      'Yugoslav identity',
      'Yugoslavia heritage',
      'Yugoslav nostalgia',
      'former Yugoslavia',
      'Yugoslav culture',
    ],
    tags: ['Identity', 'Culture', 'History', 'Nostalgia', 'Heritage'],
    relatedProducts: ['1', '2', '3'], // Link to relevant products
    content: `
      <p class="lead text-xl font-medium">To be Born in Yugoslavia is to carry a unique identity—one that transcends the borders drawn in the 1990s. It's an identity shaped by shared history, cultural fusion, and a collective memory that millions still hold dear.</p>

      <h2>A Country That No Longer Exists, Yet Lives On</h2>
      <p>Yugoslavia dissolved in the early 1990s, but those who were Born in Yugoslavia carry something that cannot be erased by political borders. It's a feeling, a set of values, and a connection to a time when the region was united under a vision of "brotherhood and unity" (<em>bratstvo i jedinstvo</em>).</p>

      <p>Whether you were born in Belgrade, Zagreb, Sarajevo, Ljubljana, Skopje, or Podgorica, you share a common thread with millions of others. This is what it means to be Born in Yugoslavia.</p>

      <hr class="my-8 border-black" />

      <h2>The Shared Cultural Heritage</h2>
      <p>Those Born in Yugoslavia grew up with a unique cultural blend that combined influences from all corners of the federation:</p>

      <ul>
        <li><strong>Music:</strong> From turbo-folk to Yugoslav rock bands like Bijelo Dugme and Azra</li>
        <li><strong>Cinema:</strong> Iconic films like "Ko to tamo peva?" and "Underground"</li>
        <li><strong>Sports:</strong> The glory days of the Yugoslav national football and basketball teams</li>
        <li><strong>Architecture:</strong> Brutalist monuments (Spomeniks) and socialist modernist buildings</li>
        <li><strong>Language:</strong> The shared Serbo-Croatian language that connected millions</li>
      </ul>

      <h2>The Yugoslav Identity: More Than Nostalgia</h2>
      <p>Being Born in Yugoslavia isn't just nostalgia for the past—it's about recognizing a shared identity that shaped who we are today. It's about remembering:</p>

      <ul>
        <li>The sense of unity across ethnic and religious lines</li>
        <li>A non-aligned foreign policy that gave Yugoslavia unique global standing</li>
        <li>Worker self-management and the Yugoslav economic model</li>
        <li>Free education and healthcare systems</li>
        <li>The cultural renaissance of the 1960s-1980s</li>
      </ul>

      <hr class="my-8 border-black" />

      <h2>The Yugoslav Aesthetic: Bureaucratic Chic</h2>
      <p>One of the most distinctive aspects of being Born in Yugoslavia was experiencing the unique visual language of the socialist state. From official documents with their heavy typewriter fonts to the brutalist architecture that dominated public spaces, there was a distinct aesthetic that defined everyday life.</p>

      <p>This "bureaucratic chic" wasn't just about government forms and stamps—it represented a commitment to functionality, clarity, and a certain austere beauty. Today, this aesthetic has become iconic, celebrated for its bold typography, geometric designs, and uncompromising visual identity.</p>

      <h2>Carrying the Heritage Forward</h2>
      <p>Today, millions of people around the world identify as Born in Yugoslavia. Whether you still live in the region or you've emigrated to North America, Australia, or Western Europe, this identity connects you to a larger community.</p>

      <p>It's visible in:</p>
      <ul>
        <li>The continued popularity of Yugoslav music and films</li>
        <li>Annual gatherings and cultural festivals celebrating Yugoslav heritage</li>
        <li>Online communities sharing memories and artifacts</li>
        <li>Fashion and design inspired by Yugoslav aesthetics</li>
        <li>The preservation and restoration of Spomenik monuments</li>
      </ul>

      <h2>The Next Generation</h2>
      <p>Even those who weren't Born in Yugoslavia—the children and grandchildren of Yugoslav families—often feel a connection to this heritage. They're discovering the music, exploring the monuments, and learning about the history that shaped their parents' and grandparents' lives.</p>

      <p>This transmission of cultural identity shows that being Born in Yugoslavia isn't just about geography or timing—it's about values, memories, and a shared understanding of where we came from.</p>

      <hr class="my-8 border-black" />

      <h2>Wear Your Heritage</h2>
      <p>Being Born in Yugoslavia is something to celebrate. It's a unique perspective, a rich cultural heritage, and a connection to millions who share your story. Whether you remember the old country firsthand or you're discovering it through family stories and historical research, this identity remains meaningful.</p>

      <p>The country may be gone, but the identity lives on—in our memories, our culture, and our continued celebration of what it meant to be Yugoslav.</p>

      <p><em>Celebrate your heritage with our <a href="/products">exclusive Born in YU apparel collection</a>. Each piece honors the bureaucratic aesthetic and brutalist design that defined Yugoslav visual culture.</em></p>
    `,
  },
  {
    id: '1',
    slug: 'complete-guide-spomenik-monuments-yugoslavia',
    title: 'The Complete Guide to Spomenik Monuments in Former Yugoslavia',
    subtitle: 'Discover the brutalist masterpieces scattered across the Balkans',
    metaDescription: 'Explore the complete guide to Spomenik monuments in former Yugoslavia. Learn about the history, locations, and architectural significance of these brutalist monuments across the Balkans.',
    author: 'Born in YU Archives',
    publishDate: '2026-01-03',
    category: 'architecture',
    featuredImage: '/blog/spomenik-guide-featured.jpg',
    featuredImageAlt: 'Aerial view of Podgarić Spomenik monument in Croatia',
    readTime: '12 min read',
    seoKeywords: [
      'spomenik monuments',
      'yugoslavia monuments',
      'brutalist architecture balkans',
      'yugoslav war memorials',
      'spomenik guide',
      'visit spomeniks',
    ],
    tags: ['Spomenik', 'Brutalism', 'Architecture', 'History', 'Travel'],
    relatedProducts: ['1', '4'], // Link to relevant products
    content: `
      <p class="lead text-xl font-medium">Scattered across the landscapes of former Yugoslavia stand some of the most striking examples of brutalist architecture in the world: the Spomeniks. These monumental structures, built between the 1960s and 1980s, commemorate the fallen soldiers and civilians of World War II.</p>

      <h2>What Are Spomeniks?</h2>
      <p>The word "spomenik" (споменик) simply means "monument" in Serbo-Croatian. However, these aren't ordinary monuments. Commissioned by President Josip Broz Tito, these abstract sculptures represent a unique fusion of brutalist architecture, socialist ideology, and futuristic design.</p>

      <hr class="my-8 border-black" />

      <h2>Top 10 Spomeniks You Must Visit</h2>

      <div class="space-y-8">
        <div class="spomenik-entry">
          <h3>1. Podgarić Memorial Complex, Croatia</h3>
          <ul class="not-prose mb-4 list-none space-y-1 text-sm font-typewriter bg-gray-100 p-4 border border-black">
            <li><strong>LOCATION:</strong> Podgarić, Moslavina, Croatia</li>
            <li><strong>GPS:</strong> 45.5167°N, 17.0167°E</li>
            <li><strong>DESIGNER:</strong> Dušan Džamonja (1967)</li>
          </ul>
          <p>This massive stainless steel structure resembles a giant flower or sunburst. One of the most photographed spomeniks, it stands on a hilltop with panoramic views of the surrounding countryside.</p>
        </div>

        <div class="spomenik-entry">
          <h3>2. Tjentište War Memorial, Bosnia and Herzegovina</h3>
          <ul class="not-prose mb-4 list-none space-y-1 text-sm font-typewriter bg-gray-100 p-4 border border-black">
            <li><strong>LOCATION:</strong> Sutjeska National Park, Bosnia and Herzegovina</li>
            <li><strong>GPS:</strong> 43.3425°N, 18.6506°E</li>
            <li><strong>DESIGNER:</strong> Miodrag Živković (1971)</li>
          </ul>
          <p>Often called the "Monument to the Revolution," this twin-winged concrete structure commemorates the Battle of Sutjeska. Its wing-like forms appear to be taking flight from the mountainside.</p>
        </div>

        <div class="spomenik-entry">
          <h3>3. Kadinjača Memorial Complex, Serbia</h3>
          <ul class="not-prose mb-4 list-none space-y-1 text-sm font-typewriter bg-gray-100 p-4 border border-black">
            <li><strong>LOCATION:</strong> Užice, Serbia</li>
            <li><strong>GPS:</strong> 43.7833°N, 19.7167°E</li>
            <li><strong>DESIGNER:</strong> Miodrag Živković (1979)</li>
          </ul>
          <p>Five massive concrete hands reach skyward in this powerful memorial. Each finger represents a different year of Yugoslav resistance during World War II.</p>
        </div>

        <div class="spomenik-entry">
          <h3>4. Petrova Gora Monument, Croatia</h3>
          <ul class="not-prose mb-4 list-none space-y-1 text-sm font-typewriter bg-gray-100 p-4 border border-black">
            <li><strong>LOCATION:</strong> Petrova Gora, Croatia</li>
            <li><strong>GPS:</strong> 45.2833°N, 15.8167°E</li>
            <li><strong>DESIGNER:</strong> Vojin Bakić (1981)</li>
          </ul>
          <p>This stainless steel and concrete flower-like structure once housed a museum. Though now abandoned and deteriorating, it remains an architectural marvel.</p>
        </div>

        <div class="spomenik-entry">
          <h3>5. Kosmaj Monument, Serbia</h3>
          <ul class="not-prose mb-4 list-none space-y-1 text-sm font-typewriter bg-gray-100 p-4 border border-black">
            <li><strong>LOCATION:</strong> Mount Kosmaj, Serbia</li>
            <li><strong>GPS:</strong> 44.4667°N, 20.5833°E</li>
            <li><strong>DESIGNER:</strong> Vojin Stojić (1971)</li>
          </ul>
          <p>Five massive wings of concrete and steel rise from the mountain peak, visible from miles away. This monument commemorates the Kosmaj Partisan Detachment.</p>
        </div>

        <div class="spomenik-entry">
          <h3>6. Jasenovac Memorial, Croatia</h3>
          <ul class="not-prose mb-4 list-none space-y-1 text-sm font-typewriter bg-gray-100 p-4 border border-black">
            <li><strong>LOCATION:</strong> Jasenovac, Croatia</li>
            <li><strong>GPS:</strong> 45.2833°N, 16.9333°E</li>
            <li><strong>DESIGNER:</strong> Bogdan Bogdanović (1966)</li>
          </ul>
          <p>A giant concrete flower blooms from the earth at this somber memorial to the victims of the Jasenovac concentration camp.</p>
        </div>

        <div class="spomenik-entry">
          <h3>7. Makljen Monument, Bosnia and Herzegovina</h3>
          <ul class="not-prose mb-4 list-none space-y-1 text-sm font-typewriter bg-gray-100 p-4 border border-black">
            <li><strong>LOCATION:</strong> Makljen, Bosnia and Herzegovina</li>
            <li><strong>GPS:</strong> 43.8500°N, 17.9500°E</li>
            <li><strong>DESIGNER:</strong> Miodrag Živković (1974)</li>
          </ul>
          <p>Massive concrete rings intersect to create an otherworldly structure that seems to defy gravity.</p>
        </div>

        <div class="spomenik-entry">
          <h3>8. Kozara Memorial, Bosnia and Herzegovina</h3>
          <ul class="not-prose mb-4 list-none space-y-1 text-sm font-typewriter bg-gray-100 p-4 border border-black">
            <li><strong>LOCATION:</strong> Kozara National Park, Bosnia and Herzegovina</li>
            <li><strong>GPS:</strong> 45.0500°N, 16.8167°E</li>
            <li><strong>DESIGNER:</strong> Dušan Džamonja (1972)</li>
          </ul>
          <p>A monumental cylindrical tower rises from the forest, its concrete walls forming a protective circle around the memorial chamber inside.</p>
        </div>

        <div class="spomenik-entry">
          <h3>9. Niš Bubanj Memorial Park, Serbia</h3>
          <ul class="not-prose mb-4 list-none space-y-1 text-sm font-typewriter bg-gray-100 p-4 border border-black">
            <li><strong>LOCATION:</strong> Niš, Serbia</li>
            <li><strong>GPS:</strong> 43.3333°N, 21.9000°E</li>
            <li><strong>DESIGNER:</strong> Ivan Sabolić (1963)</li>
          </ul>
          <p>Three massive fists rise from the earth in a powerful symbol of resistance. This early spomenik set the tone for the monuments that would follow.</p>
        </div>

        <div class="spomenik-entry">
          <h3>10. Revolution Monument Kozara, Bosnia</h3>
          <ul class="not-prose mb-4 list-none space-y-1 text-sm font-typewriter bg-gray-100 p-4 border border-black">
            <li><strong>LOCATION:</strong> Prijedor, Bosnia and Herzegovina</li>
            <li><strong>GPS:</strong> 45.0667°N, 16.8000°E</li>
            <li><strong>DESIGNER:</strong> Dušan Džamonja (1972)</li>
          </ul>
          <p>Cylindrical concrete forms create a protective circle, symbolizing the protection of civilians during the war.</p>
        </div>
      </div>

      <hr class="my-8 border-black" />

      <h2>The History Behind the Monuments</h2>
      <p>After World War II, Yugoslavia sought to create a new national identity that transcended ethnic divisions. President Tito commissioned these monuments as symbols of unity and resistance. Unlike traditional war memorials, these abstract forms were meant to represent universal ideals rather than specific ethnic or religious narratives.</p>

      <p>The monuments were designed by Yugoslavia's leading architects and sculptors, including Bogdan Bogdanović, Vojin Bakić, Miodrag Živković, and Dušan Džamonja. They were given unprecedented creative freedom, resulting in designs that pushed the boundaries of architecture and sculpture.</p>

      <h2>Current State and Preservation</h2>
      <p>Following the breakup of Yugoslavia in the 1990s, many spomeniks fell into disrepair. Some have been vandalized, while others have simply been neglected. However, there's a growing international movement to preserve and restore these monuments as important examples of 20th-century architecture.</p>

      <h2>Visiting Spomeniks: Practical Information</h2>

      <h3>Best Time to Visit</h3>
      <p>Late spring (May-June) and early autumn (September-October) offer the best weather for monument hunting. Many are located in mountainous areas where winters can be harsh.</p>

      <h3>Getting There</h3>
      <p>Most spomeniks are remote. Renting a car is highly recommended. Some monuments can be reached by public transportation and hiking, but a vehicle provides the most flexibility.</p>

      <h3>What to Bring</h3>
      <ul>
        <li>Good hiking shoes (many monuments require short hikes)</li>
        <li>Water and snacks</li>
        <li>Camera equipment</li>
        <li>GPS device or offline maps</li>
        <li>Respectful attitude (these are war memorials)</li>
      </ul>

      <h2>Photography Tips</h2>
      <p>The best light for photographing spomeniks is during golden hour (sunrise and sunset). The dramatic shadows and warm light enhance the sculptural qualities of these monuments. Drone photography has become popular for capturing their scale and relationship to the landscape.</p>

      <h2>Conclusion</h2>
      <p>Spomeniks represent a unique chapter in architectural history. These monuments stand as testaments to Yugoslavia's ambitious vision of unity through art and architecture. Whether you're an architecture enthusiast, history buff, or adventurous traveler, exploring these monuments offers a fascinating journey through the landscape and legacy of former Yugoslavia.</p>

      <p><em>Ready to celebrate Yugoslav design? Check out our <a href="/products">collection of Spomenik-inspired apparel and posters</a>.</em></p>
    `,
  },
];

export const getArticleBySlug = (slug: string): BlogArticle | undefined => {
  return ARTICLES.find((article) => article.slug === slug);
};

export const getArticlesByCategory = (category: string): BlogArticle[] => {
  if (category === 'all') return ARTICLES;
  return ARTICLES.filter((article) => article.category === category);
};

export const getLatestArticles = (limit: number = 3): BlogArticle[] => {
  return [...ARTICLES]
    .sort((a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime())
    .slice(0, limit);
};
